import { useState } from "react";
import { Greeting } from "@/components/today/Greeting";
import { AlertBanner } from "@/components/today/AlertBanner";
import { CountdownBand } from "@/components/today/CountdownBand";
import { WeatherCard } from "@/components/today/WeatherCard";
import { QuickLogStrip } from "@/components/today/QuickLogStrip";
import type { QuickLogAction } from "@/components/today/QuickLogStrip";
import { CaptureButton } from "@/components/today/CaptureButton";
import { LogMealDialog } from "@/components/today/LogMealDialog";
import { LogWeightDialog } from "@/components/today/LogWeightDialog";
import { LogMoodDialog } from "@/components/today/LogMoodDialog";
import { LogWorkoutDialog } from "@/components/today/LogWorkoutDialog";
import { LogSupplementDialog } from "@/components/today/LogSupplementDialog";
import { TodaysMeals } from "@/components/today/TodaysMeals";
import { TodayTasksSection } from "@/components/today/TodayTasksSection";
import { TodayVitals } from "@/components/today/TodayVitals";
import { RecoveryHeroCard } from "@/components/today/RecoveryHeroCard";
import { SuggestionsList } from "@/components/today/SuggestionsList";
import { Glp1ProteinStatus } from "@/components/today/Glp1ProteinStatus";
import { DailyCheckInCard } from "@/components/today/DailyCheckInCard";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { useTodayRecovery } from "@/hooks/use-today-recovery";
import { upcomingMilestones } from "@/data/milestones";

/**
 * Today (Dashboard mode).
 *
 * HOTFIX-BW19.4 PR-C Bug 9 reorder: utility surfaces above the fold,
 * Recovery card demoted to position 5. The greeting line carries a tiny
 * Recovery <score> chip so the score stays one glance away.
 *
 * Order:
 *   1. Greeting (time-of-day aware) + Recovery indicator chip
 *   2. WeatherCard (location chip + current conditions)
 *   3. CountdownBand (closest milestones)
 *   4. QuickLogStrip + Capture button (highest-frequency actions)
 *   5. RecoveryHeroCard (full fidelity, demoted from position 1)
 *   6. AlertBanner, DailyCheckIn, Suggestions, TodayTasks
 *   7. TodayVitals, GLP-1 protein, today's meals
 */

export function Today() {
  const profile = useAthleteProfile();
  const recovery = useTodayRecovery();
  const [openDialog, setOpenDialog] = useState<QuickLogAction | null>(null);

  const upcomingIn7d = upcomingMilestones(new Date(), 50).filter(
    (m) => m.daysUntil <= 7,
  ).length;

  const name = profile.data?.display_name ?? "friend";

  function pick(action: QuickLogAction) {
    setOpenDialog(action);
  }

  return (
    <article aria-labelledby="today-heading" className="haus-today">
      <style>{`
        .haus-today .haus-today-stack > * + * {
          margin-top: var(--space-6);
        }
        @media (max-width: 767px) {
          .haus-today .haus-today-stack > * + * {
            margin-top: var(--space-4);
          }
        }
      `}</style>
      <Greeting
        name={name}
        upcomingIn7d={upcomingIn7d}
        recoveryScore={recovery.data?.score ?? null}
        recoveryZone={recovery.data?.zone ?? null}
      />
      <div className="haus-today-stack">
        <WeatherCard />
        <CountdownBand />
        <QuickLogStrip onPick={pick} />
        <CaptureButton />
        <RecoveryHeroCard />
        <AlertBanner />
        <DailyCheckInCard />
        <SuggestionsList />
        <TodayTasksSection />
        <TodayVitals />
        <Glp1ProteinStatus />
        <TodaysMeals />
      </div>

      <h2 id="today-heading" style={{ position: "absolute", left: -9999 }}>
        Today
      </h2>

      {/*
        BW12.5 hotfix Issue 1: each dialog is mounted ONLY while open.
        That guarantees a fresh state machine on every reopen — no stale
        photo input, no leaked tab selection, no half-set extracted items.
        Dropping `open` from the prop boundary also keeps each dialog
        focused on the act-of-logging instead of an idle render path.
      */}
      {openDialog === "log_meal" && (
        <LogMealDialog open onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "log_weight" && (
        <LogWeightDialog open onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "log_mood" && (
        <LogMoodDialog open onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "log_workout" && (
        <LogWorkoutDialog open onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === "log_supplement" && (
        <LogSupplementDialog open onClose={() => setOpenDialog(null)} />
      )}
    </article>
  );
}
